<template>
  <div
    class="skeleton"
    :class="[`skeleton--${forma}`, { 'skeleton--parado': !animar }]"
    :style="estilo"
    role="status"
    :aria-label="rotulo"
    aria-live="polite"
  >
    <span class="so-leitor">{{ rotulo }}</span>
  </div>
</template>

<script>
/**
 * 2.8 — `Skeleton` (UI-31).
 *
 * ## O defeito
 *
 * **33 componentes tinham estado de carregamento e nenhum tinha skeleton.** Enquanto a API
 * respondia, a tela ficava vazia ou pulando — e "pulando" é o pior dos dois: o conteúdo chega, o
 * layout re-flui, e o dedo que já estava indo para um botão acerta outro.
 *
 * ## A regra que faz um skeleton servir para alguma coisa
 *
 * **Ele tem que ter a forma e o tamanho do que vai chegar.** Um retângulo genérico no lugar de um
 * card de 3 linhas não evita o pulo — só adia. É por isso que este componente pede `largura` e
 * `altura` em vez de se virar sozinho: quem sabe o tamanho do conteúdo é a tela.
 *
 * ## Por que ele varre devagar
 *
 * A varredura é de 1,6s e usa `--curva-agua`. Skeletons de biblioteca costumam varrer em 800ms
 * lineares, que num jogo calmo parece pressa — e pressa é o que o D0 proíbe. A varredura aqui diz
 * "está vindo", não "corra".
 *
 * ## `aria-live="polite"`
 *
 * Sem isso um leitor de tela não anuncia nada durante o carregamento e depois despeja a tela
 * inteira de uma vez. Com ele, o usuário ouve "carregando" e, quando o conteúdo entra, ouve o
 * conteúdo.
 */
export default {
  name: 'FishSkeleton',
  props: {
    forma: {
      type: String,
      default: 'bloco',
      validator: (v) => ['bloco', 'texto', 'titulo', 'encaixe', 'circulo'].includes(v),
    },
    largura: { type: String, default: '' },
    altura: { type: String, default: '' },
    rotulo: { type: String, default: 'Carregando' },
    animar: { type: Boolean, default: true },
  },
  computed: {
    estilo() {
      const e = {};
      if (this.largura) e.width = this.largura;
      if (this.altura) e.height = this.altura;
      return e;
    },
  },
};
</script>

<style scoped>
.skeleton {
  /* O degradê é o que varre. `200% 100%` de tamanho com a posição animada de -200% a 200% faz a
   * faixa clara atravessar a caixa inteira — é a técnica padrão, e a única que não precisa de um
   * pseudo-elemento posicionado por cima. */
  background: linear-gradient(
    90deg,
    var(--papel-sombra) 25%,
    var(--papel-luz) 50%,
    var(--papel-sombra) 75%
  );
  background-size: 200% 100%;
  animation: esqueleto 1.6s var(--curva-agua) infinite;
}

.skeleton--parado {
  animation: none;
}

.skeleton--bloco {
  width: 100%;
  height: 80px;
}

/* A linha de texto não vai até a borda: texto de verdade quase nunca vai, e uma pilha de barras
 * exatamente do mesmo comprimento lê como tabela, não como parágrafo. */
.skeleton--texto {
  width: 92%;
  height: 1em;
  margin-bottom: var(--e-2);
}

.skeleton--texto:last-child {
  width: 64%;
}

.skeleton--titulo {
  width: 45%;
  height: 1.5em;
  margin-bottom: var(--e-3);
}

.skeleton--encaixe {
  width: 100%;
  aspect-ratio: 1;
  min-height: var(--alvo-minimo);
}

.skeleton--circulo {
  width: 48px;
  height: 48px;
  border-radius: var(--r-circulo);
}

/* Com `prefers-reduced-motion` a varredura para, mas o skeleton continua existindo: ele não é
 * decoração, é o que segura o layout no lugar até o conteúdo chegar. */
@media (prefers-reduced-motion: reduce) {
  .skeleton {
    animation: none;
    background: var(--papel-sombra);
  }
}
</style>
